import {
  fork,
  put,
  all,
  call,
  takeLeading,
  takeLatest,
  take,
  cancel,
  select,
} from 'redux-saga/effects';
import allowedRoles from '../../config/assignAllowedRoles';
import types from './actionTypes';
import * as usersActions from './actions';
import { selectUser } from '../../selectors/user';
import {
  MAX_SELECTED_ACQUISITION_ORDERS,
  applyBatchEditMode,
  batchEditModes,
} from '../../config/acquisitionOrders';
import {
  getFirestore,
  query,
  collection,
  doc,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import rsf from '../../helpers/firebase';
import toastr from 'toastr';
import { toDateFirebase } from '../../helpers/sharedFunction';

const db = getFirestore(rsf.app);

function* createUserSaga({ user }) {
  try {
    const db = getFirestore(rsf.app);
    const usersRef = collection(db, 'users');
    yield call(rsf.firestore.addDocument, usersRef, {
      ...user,
      createdAt: serverTimestamp(),
    });
    yield put(usersActions.createUserSuccess(user));
    toastr.success('User created!', '');
  } catch (error) {
    yield put(usersActions.createUserFailure(error));
  }
}

function* updateUserSaga({ user }) {
  try {
    const companyId = yield select((state) => state.Dashboard.companyId);
    const selectedUser = yield select(selectUser(user.id));

    const acquisitionOrderIds =
      Array.isArray(user.acquisitionOrderIds) && user.acquisitionOrderIds.length
        ? {
            ...selectedUser.acquisitionOrderIds,
            [`${companyId}`]: user.acquisitionOrderIds,
          }
        : { ...selectedUser.acquisitionOrderIds, [`${companyId}`]: null };

    const usersRef = doc(db, 'users', user.id);
    delete user.id;

    yield call(
      rsf.firestore.setDocument,
      usersRef,
      {
        ...user,
        acquisitionOrderIds,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
    yield put(usersActions.updateUserSuccess(user));
    toastr.success('User updated!', '');
  } catch (error) {
    yield put(usersActions.updateUserFailure(error));
  }
}

function* updateUsersAcquisitionOrdersSaga({
  users,
  acquisitionOrderIds,
  mode,
}) {
  try {
    if (!Array.isArray(users) || !users.length)
      throw new Error('Users must be a not empty array!');
    if (!Array.isArray(acquisitionOrderIds) || !acquisitionOrderIds.length)
      throw new Error('AcquisitionOrderIds must be a not empty array!');
    if (!Object.values(batchEditModes).includes(mode))
      throw new Error('Edit mode is not valid!');

    const companyId = yield select((state) => state.Dashboard.companyId);
    if (!companyId) throw new Error('CompanyId is mandatory!');

    let logs = [];

    for (let i = 0; i < users.length; i++) {
      const selectedUser = (yield select(selectUser(users[i].id))) || users[i];
      try {
        const currentAcquisitionOrderIds =
          selectedUser.acquisitionOrderIds?.[companyId] || [];

        const updatedAcquisitionOrderIds = applyBatchEditMode(
          currentAcquisitionOrderIds,
          acquisitionOrderIds,
          mode,
        );

        if (
          mode !== batchEditModes.DELETE &&
          updatedAcquisitionOrderIds.length > MAX_SELECTED_ACQUISITION_ORDERS
        )
          throw new Error(
            `Skipped: ${updatedAcquisitionOrderIds.length} acquisition orders exceed the limit of ${MAX_SELECTED_ACQUISITION_ORDERS}`,
          );

        const userRef = doc(db, 'users', selectedUser.id);

        yield call(
          rsf.firestore.setDocument,
          userRef,
          {
            acquisitionOrderIds: {
              ...selectedUser.acquisitionOrderIds,
              [`${companyId}`]: updatedAcquisitionOrderIds.length
                ? updatedAcquisitionOrderIds
                : null,
            },
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );

        logs.push({
          userId: selectedUser.id,
          displayName: selectedUser.displayName || selectedUser.email,
          response: `${currentAcquisitionOrderIds.length} -> ${updatedAcquisitionOrderIds.length} acquisition orders`,
          status: 200,
        });
      } catch (error) {
        logs.push({
          userId: selectedUser.id,
          displayName: selectedUser.displayName || selectedUser.email,
          response: error.message,
          status: 500,
        });
      }
    }

    yield put(usersActions.updateUsersAcquisitionOrdersSuccess(logs));

    const updatedUsers = logs.filter(({ status }) => status === 200).length;
    toastr.success(`Acquisition Orders updated for ${updatedUsers} users!`, '');
  } catch (error) {
    yield put(usersActions.updateUsersAcquisitionOrdersFailure(error));
    toastr.error(error.message, 'Error');
  }
}

const userTransformer = (payload, params) => {
  const admin = params.user;
  const roles = allowedRoles[admin.role];
  let users = [];

  payload.forEach((user) => {
    const data = user.data();
    if (roles.includes(data.role) || user.id === admin.id) {
      users.push({
        id: user.id,
        ...data,
        ...(data.createdAt && {
          createdAt: toDateFirebase(user, data).toDate(),
        }),
        ...(data.updatedAt && {
          updatedAt: toDateFirebase(user, data, 'updatedAt').toDate(),
        }),
      });
    }
  });

  return users;
};

function* syncUsersSaga() {
  const user = yield select((state) => state.Auth.admin);
  const usersRef = query(collection(db, 'users'), orderBy('createdAt', 'desc'));

  const task = yield fork(rsf.firestore.syncCollection, usersRef, {
    successActionCreator: usersActions.syncUsersSuccess,
    failureActionCreator: usersActions.syncUsersFailure,
    params: {
      user,
    },
    transform: userTransformer,
  });

  yield take(types.RESET_USER_STATE);
  yield cancel(task);
}

function* userSaga() {
  yield all([
    takeLatest(types.SYNC_USERS.REQUEST, syncUsersSaga),
    takeLeading(types.CREATE_USER.REQUEST, createUserSaga),
    takeLeading(types.UPDATE_USER.REQUEST, updateUserSaga),
    takeLeading(
      types.UPDATE_USERS_ACQUISITION_ORDERS.REQUEST,
      updateUsersAcquisitionOrdersSaga,
    ),
  ]);
}

export default userSaga;
